import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { ChoiceContent, PictureContent, TrueFalseContent } from '../contentTypes'
import type { RendererProps } from './GapRenderers'

/**
 * Рендереры типов с выбором. Ответы: `{ selected: [0, 2] }` для вариантов,
 * `{ statements: ["true", …] }` для верно/неверно, `{ hotspots: […] }` или
 * `{ selected: 1 }` для картинок.
 */

export function ChoiceRenderer({ content, answers, onChange, readOnly }: RendererProps<ChoiceContent>) {
  const selected = Array.isArray(answers.selected) ? (answers.selected as number[]) : []
  const multiple = content.multiple === true

  const toggle = (index: number) => {
    if (multiple) {
      const next = selected.includes(index) ? selected.filter((item) => item !== index) : [...selected, index]
      onChange({ ...answers, selected: next })

      return
    }

    onChange({ ...answers, selected: [index] })
  }

  return (
    <Stack spacing={2}>
      <Typography>{content.question}</Typography>

      {multiple ? (
        <Stack>
          {content.options.map((option, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox checked={selected.includes(index)} disabled={readOnly} onChange={() => toggle(index)} />
              }
              label={option.text}
            />
          ))}
        </Stack>
      ) : (
        <RadioGroup value={selected[0] ?? -1}>
          {content.options.map((option, index) => (
            <FormControlLabel
              key={index}
              value={index}
              control={<Radio disabled={readOnly} onChange={() => toggle(index)} />}
              label={option.text}
            />
          ))}
        </RadioGroup>
      )}
    </Stack>
  )
}

export function TrueFalseRenderer({ content, answers, onChange, readOnly }: RendererProps<TrueFalseContent>) {
  const { t } = useTranslation()
  const given = Array.isArray(answers.statements) ? (answers.statements as string[]) : []

  const set = (index: number, value: string) => {
    const next = [...given]
    next[index] = value
    onChange({ ...answers, statements: next })
  }

  return (
    <Stack spacing={2}>
      {content.text && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{content.text}</Typography>}

      {content.statements.map((statement, index) => (
        <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Typography sx={{ flex: 1 }}>{statement.text}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={given[index] ?? null}
            disabled={readOnly}
            onChange={(_, value) => value && set(index, String(value))}
          >
            <ToggleButton value="true">{t('exercises.run.true')}</ToggleButton>
            <ToggleButton value="false">{t('exercises.run.false')}</ToggleButton>
            {content.allow_not_stated && <ToggleButton value="not_stated">{t('exercises.run.notStated')}</ToggleButton>}
          </ToggleButtonGroup>
        </Stack>
      ))}
    </Stack>
  )
}

export function PictureRenderer({ content, answers, onChange, readOnly }: RendererProps<PictureContent>) {
  const { t } = useTranslation()

  if (content.variant === 'pick_image') {
    const selected = typeof answers.selected === 'number' ? (answers.selected as number) : null

    return (
      <Stack spacing={2}>
        {content.prompt && <Typography>{content.prompt}</Typography>}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {(content.options ?? []).map((option, index) => (
            <Box
              key={index}
              onClick={() => !readOnly && onChange({ ...answers, selected: index })}
              sx={{
                p: 0.5,
                border: 3,
                borderRadius: 1,
                borderColor: selected === index ? 'primary.main' : 'transparent',
                cursor: readOnly ? 'default' : 'pointer',
              }}
            >
              <Box
                component="img"
                src={option.image_url ?? option.image_path}
                alt=""
                sx={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 1, display: 'block' }}
              />
            </Box>
          ))}
        </Stack>
      </Stack>
    )
  }

  const given = Array.isArray(answers.hotspots) ? (answers.hotspots as string[]) : []

  return (
    <Stack spacing={2}>
      <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
        <Box
          component="img"
          src={content.image_url ?? content.image_path ?? ''}
          alt=""
          sx={{ maxWidth: '100%', display: 'block', borderRadius: 1 }}
        />
        {(content.hotspots ?? []).map((hotspot, index) => {
          const w = hotspot.w ?? 0
          const h = hotspot.h ?? 0
          // Без размеров область рисуется точкой: так размечены задания,
          // созданные до выделения рамкой
          const isPoint = w <= 0 || h <= 0

          return (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                left: `${(hotspot.x - w / 2) * 100}%`,
                top: `${(hotspot.y - h / 2) * 100}%`,
                display: 'flex',
                ...(isPoint
                  ? {
                      transform: 'translate(-50%, -50%)',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                    }
                  : {
                      width: `${w * 100}%`,
                      height: `${h * 100}%`,
                      borderRadius: 1,
                      border: 2,
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(103, 58, 183, 0.18)',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }),
              }}
            >
              {isPoint ? (
                index + 1
              ) : (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: '10px',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: 12,
                    lineHeight: '20px',
                    textAlign: 'center',
                  }}
                >
                  {index + 1}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>

      <Stack spacing={1}>
        {(content.hotspots ?? []).map((_, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <Typography sx={{ width: 28 }}>{index + 1}.</Typography>
            <Box
              component="input"
              value={given[index] ?? ''}
              disabled={readOnly}
              placeholder={t('exercises.run.hotspotPlaceholder')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const next = [...given]
                next[index] = event.target.value
                onChange({ ...answers, hotspots: next })
              }}
              sx={{
                flex: 1,
                p: 1,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'transparent',
                color: 'text.primary',
                font: 'inherit',
              }}
            />
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}
